import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { adminApi } from "../api";
import { Panel } from "../components/DataTable";
import { Target, CheckCircle, AlertCircle, TrendingUp, DollarSign, ShoppingBag, PieChart, Search } from "lucide-react";
import { formatMoney } from "@melhot/shared/money";

export function TrackingPage() {
  const pixelQuery = useQuery({
    queryKey: ["admin-pixel-settings"],
    queryFn: adminApi.pixelSettings
  });

  const attributionQuery = useQuery({
    queryKey: ["admin-traffic-attribution"],
    queryFn: adminApi.trafficAttribution,
    refetchInterval: 10000
  });

  const updatePixel = useMutation({
    mutationFn: adminApi.updatePixelSettings,
    onSuccess: () => {
      pixelQuery.refetch();
    }
  });

  const [conversionId, setConversionId] = useState("AW-18384540592");
  const [purchaseLabel, setPurchaseLabel] = useState("vO97CI-L_d8cELCnt75E");
  const [beginCheckoutLabel, setBeginCheckoutLabel] = useState("0LqLCJWL_d8cELCnt75E");
  const [addToCartLabel, setAddToCartLabel] = useState("ZfIMCJKL_d8cELCnt75E");
  const [pageViewLabel, setPageViewLabel] = useState("H2ZVCJiL_d8cELCnt75E");
  const [addPaymentInfoLabel, setAddPaymentInfoLabel] = useState("");
  const [addShippingInfoLabel, setAddShippingInfoLabel] = useState("");
  const [enhancedConversionsEnabled, setEnhancedConversionsEnabled] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");

  useEffect(() => {
    if (pixelQuery.data?.googleAds) {
      setConversionId(pixelQuery.data.googleAds.conversionId || "AW-18384540592");
      setPurchaseLabel(pixelQuery.data.googleAds.purchaseLabel || "vO97CI-L_d8cELCnt75E");
      setBeginCheckoutLabel(pixelQuery.data.googleAds.beginCheckoutLabel || "0LqLCJWL_d8cELCnt75E");
      setAddToCartLabel(pixelQuery.data.googleAds.addToCartLabel || "ZfIMCJKL_d8cELCnt75E");
      setPageViewLabel(pixelQuery.data.googleAds.pageViewLabel || "H2ZVCJiL_d8cELCnt75E");
      setAddPaymentInfoLabel(pixelQuery.data.googleAds.addPaymentInfoLabel || "");
      setAddShippingInfoLabel(pixelQuery.data.googleAds.addShippingInfoLabel || "");
      setEnhancedConversionsEnabled(pixelQuery.data.googleAds.enhancedConversionsEnabled ?? true);
    }
  }, [pixelQuery.data]);

  const handleSave = () => {
    setSaveSuccess(false);
    updatePixel.mutate(
      {
        googleAds: {
          conversionId: conversionId.trim(),
          purchaseLabel: purchaseLabel.trim(),
          beginCheckoutLabel: beginCheckoutLabel.trim(),
          addToCartLabel: addToCartLabel.trim(),
          pageViewLabel: pageViewLabel.trim(),
          addPaymentInfoLabel: addPaymentInfoLabel.trim(),
          addShippingInfoLabel: addShippingInfoLabel.trim(),
          enhancedConversionsEnabled
        }
      },
      {
        onSuccess: () => {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 4000);
        }
      }
    );
  };

  const attributions = attributionQuery.data ?? [];
  const filteredAttributions = attributions.filter(item => 
    !filterQuery || 
    item.source.toLowerCase().includes(filterQuery.toLowerCase()) || 
    item.campaign.toLowerCase().includes(filterQuery.toLowerCase()) || 
    item.medium.toLowerCase().includes(filterQuery.toLowerCase())
  );

  const totalRevenue = attributions.reduce((sum, item) => sum + (item.revenue || 0), 0);
  const totalPaid = attributions.reduce((sum, item) => sum + (item.paidCount || 0), 0);
  const totalCheckouts = attributions.reduce((sum, item) => sum + (item.checkouts || 0), 0);
  const topSource = attributions.length > 0 ? attributions[0].source : "N/A";

  return (
    <div className="space-y-6">
      <Panel title="Dashboard de Atribuição de Tráfego (UTMs & Vendas)">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="flex items-center justify-between mb-1 text-emerald-800">
              <span className="text-xs font-semibold uppercase">Receita Rastreada</span>
              <DollarSign className="w-4 h-4 text-emerald-600" />
            </div>
            <p className="text-xl font-bold text-emerald-950">{formatMoney(totalRevenue)}</p>
          </div>
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center justify-between mb-1 text-blue-800">
              <span className="text-xs font-semibold uppercase">Vendas Pagas</span>
              <ShoppingBag className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-xl font-bold text-blue-950">{totalPaid}</p>
          </div>
          <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
            <div className="flex items-center justify-between mb-1 text-purple-800">
              <span className="text-xs font-semibold uppercase">Checkouts Iniciados</span>
              <TrendingUp className="w-4 h-4 text-purple-600" />
            </div>
            <p className="text-xl font-bold text-purple-950">{totalCheckouts}</p>
          </div>
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-center justify-between mb-1 text-amber-800">
              <span className="text-xs font-semibold uppercase">Top Origem</span>
              <PieChart className="w-4 h-4 text-amber-600" />
            </div>
            <p className="text-sm font-bold text-amber-950 truncate">{topSource}</p>
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Filtrar por origem, campanha ou meio..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="field w-full pl-9 text-xs"
            />
          </div>
          <span className="text-xs text-slate-500 font-medium">
            Exibindo {filteredAttributions.length} origens de tráfego
          </span>
        </div>

        <div className="overflow-x-auto border border-line rounded-lg">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 border-b border-line text-slate-700 font-semibold uppercase">
              <tr>
                <th className="px-4 py-3">Origem (utm_source / src)</th>
                <th className="px-4 py-3">Campanha (utm_campaign)</th>
                <th className="px-4 py-3">Meio (utm_medium)</th>
                <th className="px-4 py-3 text-center">Checkouts</th>
                <th className="px-4 py-3 text-center">Vendas Pagas</th>
                <th className="px-4 py-3 text-right">Receita (R$)</th>
                <th className="px-4 py-3 text-right">Taxa Conv. (%)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {filteredAttributions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    {attributionQuery.isLoading ? "Carregando atribuição de tráfego..." : "Nenhuma origem de tráfego registrada ainda."}
                  </td>
                </tr>
              ) : (
                filteredAttributions.map((row, idx) => {
                  const convRate = row.checkouts > 0 ? ((row.paidCount / row.checkouts) * 100).toFixed(1) : "0.0";
                  return (
                    <tr key={idx} className="hover:bg-slate-50 transition">
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.source}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono">{row.campaign}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono">{row.medium}</td>
                      <td className="px-4 py-3 text-center font-medium text-slate-700">{row.checkouts}</td>
                      <td className="px-4 py-3 text-center font-bold text-emerald-600">{row.paidCount}</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900">{formatMoney(row.revenue)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-blue-600">{convRate}%</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Rastreamento & Pixels (Google Ads)">
        <div className="flex items-start gap-3 mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200 text-blue-950 text-xs leading-relaxed">
          <Target className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm mb-1 text-blue-900">Gerenciador Único do Google Ads</p>
            <p>
              Preencha o seu <strong>Google Ads Conversion ID</strong> e os rótulos de conversão das suas ações de tráfego.
              A sua loja <strong>Shopify</strong> e o seu <strong>Checkout Cloudflare</strong> estão sincronizados automaticamente!
            </p>
          </div>
        </div>

        <div className="space-y-4 max-w-xl">
          <div>
            <label className="block text-xs font-semibold mb-1 text-slate-700">
              Google Ads Conversion ID <span className="text-red-500">*</span>
            </label>
            <input
              className="field w-full font-mono text-sm"
              value={conversionId}
              onChange={(e) => setConversionId(e.target.value)}
              placeholder="AW-18384540592"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 text-slate-700">Compra (Purchase Label)</label>
              <input
                className="field w-full font-mono text-sm"
                value={purchaseLabel}
                onChange={(e) => setPurchaseLabel(e.target.value)}
                placeholder="vO97CI-L_d8cELCnt75E"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 text-slate-700">Adicionar ao Carrinho Label</label>
              <input
                className="field w-full font-mono text-sm"
                value={addToCartLabel}
                onChange={(e) => setAddToCartLabel(e.target.value)}
                placeholder="ZfIMCJKL_d8cELCnt75E"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 text-slate-700">Iniciar Finalização de Compra</label>
              <input
                className="field w-full font-mono text-sm"
                value={beginCheckoutLabel}
                onChange={(e) => setBeginCheckoutLabel(e.target.value)}
                placeholder="0LqLCJWL_d8cELCnt75E"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 text-slate-700">Visualização de Página Label</label>
              <input
                className="field w-full font-mono text-sm"
                value={pageViewLabel}
                onChange={(e) => setPageViewLabel(e.target.value)}
                placeholder="H2ZVCJiL_d8cELCnt75E"
              />
            </div>
          </div>

          <div className="p-3 rounded-md border border-line bg-slate-50 flex items-start gap-2">
            <input
              type="checkbox"
              id="enhanced_conv_tab"
              checked={enhancedConversionsEnabled}
              onChange={(e) => setEnhancedConversionsEnabled(e.target.checked)}
              className="mt-0.5 rounded text-brand focus:ring-brand"
            />
            <label htmlFor="enhanced_conv_tab" className="text-xs font-medium text-slate-700 cursor-pointer">
              Ativar <strong>Google Ads Enhanced Conversions</strong> (Conversões Avançadas com envio criptografado de E-mail, Telefone e Endereço)
            </label>
          </div>

          <div className="pt-3 flex items-center gap-3">
            <button
              className="btn btn-primary px-6"
              onClick={handleSave}
              disabled={updatePixel.isPending}
            >
              {updatePixel.isPending ? "Salvando..." : "Salvar Configurações"}
            </button>
            {saveSuccess && (
              <span className="inline-flex items-center gap-1 text-xs text-trust font-semibold bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-md">
                <CheckCircle className="w-4 h-4 text-trust" /> Salvo com sucesso!
              </span>
            )}
            {updatePixel.isError && (
              <span className="inline-flex items-center gap-1 text-xs text-red-600 font-semibold bg-red-50 border border-red-200 px-3 py-1.5 rounded-md">
                <AlertCircle className="w-4 h-4 text-red-600" /> Ocorreu um erro ao salvar.
              </span>
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
}
